import { API, TV_API, unwrapData } from '../axios-instance'
import { Comment, ThreadType } from '../comment/types'
import {
  FederatedSearchRecipientsParams,
  FederatedSearchRecipientsResults,
  PageResult,
} from '../shared/types'
import {
  CountResponse,
  CreateCommentParams,
  CreateEndorsementParams,
  CreateReactionParams,
  FlagParams,
  GetCommentsParams,
  ListCommentsReactionsParams,
  ListEndorsementsParams,
  ListReactionsParams,
  UpdateWallPostCommentParams,
  UpdateWallPostMessageParams,
} from '../shared/wallPost/types'
import {
  CreateRecognitionParams,
  FlagCommentParams,
  ListPointPresetsParams,
  ListRecognitionRecipientsSummaryParams,
  ListRecognitionsParams,
  ManagerDirectReportRanking,
  ManagerRanking,
  PeerRecognition,
  PointPreset,
  PointPresetParam,
  PreviewRecipientsResults,
  Recognition,
  RecognitionEmbeds,
  RecognitionEndorsement,
  RecognitionGoal,
  RecognitionParams,
  RecognitionReaction,
  RecognitionSummaryRecipient,
  RecognitionWithFlags,
  SendTo,
} from './types'

export const createRecognition = async (params: CreateRecognitionParams) => {
  return await API.post<Recognition>('/recognition/recognitions', params).then(unwrapData)
}

export const editRecognition = async (recognitionId: string, params: CreateRecognitionParams) => {
  return await API.put<Recognition>(`/recognition/recognitions/${recognitionId}`, params).then(
    unwrapData,
  )
}

export const listRecognitionRecipients = async (params: FederatedSearchRecipientsParams) => {
  return await API.get<FederatedSearchRecipientsResults>('/recognition/recipients', {
    params,
  }).then(unwrapData)
}

export const previewRecipientCount = async (params: { sendTo: SendTo[]; delegatorId?: string }) => {
  return await API.post<PreviewRecipientsResults>('/recognition/recipients/preview', params).then(
    unwrapData,
  )
}

export const listRecognitions = async (params?: ListRecognitionsParams) => {
  return await API.get<PageResult<Recognition>>('/recognition/recognitions', { params }).then(
    unwrapData,
  )
}

export const listRecognitionRecipientsSummary = async (
  params: ListRecognitionRecipientsSummaryParams,
) => {
  return await API.get<PageResult<RecognitionSummaryRecipient>>('/recognition/recipients/summary', {
    params,
  }).then(unwrapData)
}

export const endorseRecognition = async ({ amount, id }: CreateEndorsementParams) => {
  return await API.post<RecognitionEndorsement>(`recognition/recognitions/${id}/endorsements`, {
    amount,
  }).then(unwrapData)
}

export const listRecognitionEndorsements = async (
  recognitionId: string,
  params: ListEndorsementsParams,
) => {
  return await API.get<PageResult<RecognitionEndorsement>>(
    `recognition/recognitions/${recognitionId}/endorsements`,
    { params },
  ).then(unwrapData)
}

export const listFlaggedRecognition = async (params?: ListRecognitionsParams) => {
  return await API.get<PageResult<RecognitionWithFlags>>('/recognition/recognitions/flagged', {
    params,
  }).then(unwrapData)
}

export const flagRecognition = async (id: string) => {
  return await API.put<boolean>(`recognition/recognitions/${id}/flags`).then(unwrapData)
}

export const restoreFlaggedRecognition = async ({ postId }: FlagParams) => {
  return await API.delete<boolean>(`recognition/recognitions/${postId}/flags`)
}

export const reactToRecognition = async ({ reaction, postId }: CreateReactionParams) => {
  return await API.put<RecognitionReaction>(`recognition/recognitions/${postId}/reactions`, {
    reaction,
    recognitionId: postId,
  }).then(unwrapData)
}

export const listRecognitionReactions = async (postId: string, params: ListReactionsParams) => {
  return await API.get<PageResult<RecognitionReaction>>(
    `recognition/recognitions/${postId}/reactions`,
    {
      params,
    },
  ).then(unwrapData)
}

export const createRecognitionComment = async ({
  body,
  id,
  tenantMemberId,
  threadId,
  gifs,
}: CreateCommentParams) => {
  return await API.post<Comment>(`recognition/recognitions/${id}/comments`, {
    body,
    tenantMemberId,
    threadId,
    threadType: ThreadType.RECOGNITION,
    gifs: gifs || [],
  }).then(unwrapData)
}

export const listRecognitionComments = async (id: string, params?: GetCommentsParams) => {
  return await API.get<PageResult<Comment>>(`/recognition/recognitions/${id}/comments`, {
    params,
  }).then(unwrapData)
}

export const flagRecognitionComment = async ({ id, commentId }: FlagCommentParams) => {
  return await API.put<boolean>(`recognition/recognitions/${id}/comments/${commentId}/flags`).then(
    unwrapData,
  )
}

export const reactToRecognitionComment = async ({
  reaction,
  postId,
  commentId,
}: CreateReactionParams) => {
  return await API.put<RecognitionReaction>(
    `recognition/recognitions/${postId}/comments/${commentId}/reactions`,
    {
      reaction,
      recognitionId: postId,
      commentId,
    },
  ).then(unwrapData)
}

export const removeRecognitionReaction = async ({
  postId,
}: Pick<CreateReactionParams, 'postId'>) => {
  return await API.delete<boolean>(`recognition/recognitions/${postId}/reactions`).then(unwrapData)
}

export const removeRecognitionCommentReaction = async ({
  postId,
  commentId,
}: Pick<CreateReactionParams, 'postId' | 'commentId'>) => {
  return await API.delete<boolean>(
    `recognition/recognitions/${postId}/comments/${commentId}/reactions`,
  ).then(unwrapData)
}

export const listRecognitionCommentReactions = async (
  commentId: string,
  id: string,
  params: ListCommentsReactionsParams,
) => {
  return await API.get<PageResult<RecognitionReaction>>(
    `recognition/recognitions/${id}/comments/${commentId}/reactions`,
    { params },
  ).then(unwrapData)
}

export const listSpotlightRecognitions = async (embeds: RecognitionEmbeds[], pageSize: number) => {
  return await API.get<Recognition[]>('/recognition/recognitions/spotlight', {
    params: {
      pageSize,
      embeds,
    },
  }).then(unwrapData)
}

export const recognitionCount = async (params: RecognitionParams) => {
  return await API.get<CountResponse>('/recognition/recognitions/count', { params }).then(
    unwrapData,
  )
}

export const deleteRecognition = async (recognitionId: string) => {
  return await API.delete<boolean>(`/recognition/recognitions/${recognitionId}`)
}

export const editRecognitionMessage = async (
  params: UpdateWallPostMessageParams,
  recognitionId: string,
) => {
  return await API.patch<Recognition>(`/recognition/recognitions/${recognitionId}`, params).then(
    unwrapData,
  )
}

export const getRecognition = async (recognitionId: string, embeds?: RecognitionEmbeds[]) => {
  return await API.get<Recognition>(`/recognition/recognitions/${recognitionId}`, {
    params: { embeds },
  }).then(unwrapData)
}

export const getRecognitionGoal = async () => {
  return await API.get<RecognitionGoal>('/recognition/recognitions/goal').then(unwrapData)
}

export const managerRankings = async (limit: number) => {
  return await API.get<ManagerRanking>('/recognition/managers/rankings', {
    params: { limit },
  }).then(unwrapData)
}

export const managerDirectReportRankings = async () => {
  return await API.get<ManagerDirectReportRanking[]>(
    '/recognition/managers/directReportRankings',
  ).then(unwrapData)
}

export const getPeerRecognitions = async (limit: number) => {
  return await API.get<PeerRecognition[]>('/recognition/peers', {
    params: { limit },
  }).then(unwrapData)
}

export const listTvRecognitions = async (token: string, embeds?: RecognitionEmbeds[]) => {
  return await TV_API.get<Recognition[]>(`/recognition/tv/${token}/recognitions`, {
    params: { embeds },
  }).then(unwrapData)
}

export const updatePointPresets = async (params: PointPresetParam[]) => {
  return await API.put<PointPreset[]>('/recognition/pointPresets', params).then(unwrapData)
}

export const listPointPresets = async (params?: ListPointPresetsParams) => {
  return await API.get<PointPreset[]>('/recognition/pointPresets', { params }).then(unwrapData)
}

export const editRecognitionComment = async (
  params: UpdateWallPostCommentParams,
  recognitionId: string,
  commentId: string,
) => {
  return await API.patch<Comment>(
    `/recognition/recognitions/${recognitionId}/comments/${commentId}`,
    params,
  ).then(unwrapData)
}

// delegation
export const createDelegationRecognition = async (
  delegatorId: string,
  params: CreateRecognitionParams,
) => {
  return await API.post<Recognition>(
    `/recognition/recognitions/delegator/${delegatorId}`,
    params,
  ).then(unwrapData)
}
